import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControlLabel, Switch, Autocomplete, Chip, Stack, Alert, IconButton, InputAdornment, Typography, Box } from '@mui/material';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import CloseIcon from '@mui/icons-material/Close';
import { fetchAllRoles, createUser } from '../../controllers/userManagementController';

const CreateUserDialog = ({ open, onClose, onCreated }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [allRoles, setAllRoles] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      try {
        const r = await fetchAllRoles();
        if (mounted) setAllRoles(r || []);
      } catch (e) {
        if (mounted) setError(e?.message || 'Roller yüklenemedi');
      }
    })();
    return () => {
      mounted = false;
    };
  }, [open]);

  const reset = () => {
    setUsername('');
    setPassword('');
    setIsActive(true);
    setRoles([]);
    setError('');
    setSuccess('');
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const created = await createUser({ username, password, isActive, roles });
      setSuccess('Kullanıcı oluşturuldu');
      onCreated?.(created);
      setTimeout(handleClose, 800);
    } catch (err) {
      setError(err?.message || 'Kullanıcı oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle 
        sx={{ 
          fontSize: '18px', 
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Yeni Kullanıcı
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Kullanıcı Adı
              </Typography>
              <TextField
                fullWidth
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder=""
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Şifre
              </Typography>
            <TextField
                fullWidth
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
                placeholder=""
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                        sx={{ color: 'text.secondary' }}
                    >
                        {showPassword ? <RemoveRedEyeIcon fontSize="small" /> : <RemoveRedEyeOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Roller
              </Typography>
            <Autocomplete
              multiple
              options={allRoles}
              value={roles}
              onChange={(e, val) => setRoles(val)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder=""
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '5px',
                        py: 0,
                        px: 0,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiAutocomplete-inputRoot': {
                        py: '2.5px',
                        px: '12px',
                      },
                      '& .MuiAutocomplete-input': {
                        py: 0,
                        px: 0,
                        fontSize: '14px',
                      },
                      '& .MuiInputBase-input': {
                        py: 1.2,
                        px: 1.5,
                        fontSize: '14px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                      '& .MuiChip-root': {
                        height: '24px',
                        margin: '2px',
                      },
                    }}
                  />
                )}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Durum
              </Typography>
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                label={isActive ? 'Aktif' : 'Pasif'}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '13px',
                  },
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            sx={{
              textTransform: 'none',
              color: '#0E4D94',
            }}
          >
            İptal
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              borderRadius: '5px',
              textTransform: 'none',
              fontSize: '14px',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
              },
            }}
          >
            Oluştur
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateUserDialog;


